// import { EnvService } from '@services';
import {
  ConditionInterface,
  MapMarkerTimedInterface,
  MapMarkerTimeReferenceInterface,
} from '@mzima-client/sdk';
import dayjs from 'dayjs';
import L, { divIcon, marker } from 'leaflet';
import 'leaflet-extra-markers';
import { EnvService } from '../services/env.service';

export const pointIcon = (color: string, type: string = 'default') => {
  // Test string to make sure that it does not contain injection
  color = color && /^[a-zA-Z0-9#]+$/.test(color) ? `#${color}` : 'var(--color-neutral-100)';
  const size: any = [30, 40];
  // var iconicSprite = require('ushahidi-platform-pattern-library/assets/img/iconic-sprite.svg');

  return divIcon({
    className: 'custom-map-marker',
    html: `
    <svg class="iconic" style="height: 100%; width: 100%; fill:${color};">
      <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="assets/markers.svg#${type}"></use>
    </svg>
    <span class="iconic-bg" style="background-color:${color};"></span>
    `,
    iconSize: size,
    iconAnchor: [size[0] / 2, size[1]],
    popupAnchor: [0, 0 - size[1]],
  });
};

export const isTriggered = (
  timeReference: MapMarkerTimeReferenceInterface,
  condition: ConditionInterface,
) => {
  if (!timeReference[condition.key]) {
    return false;
  }

  // Date of when created, updated, or approved (condition.key)
  const timeRefObj = dayjs(timeReference[condition.key]);
  const todayObj = dayjs();

  // newTimeRefObj = N time units after it was created/updated/reported
  const newTimeRefObj = timeRefObj.add(condition.time_value, condition.time_unit);

  switch (condition.operator) {
    case 'eq': // todayObj == newTimeRefObj
      return todayObj.isSame(newTimeRefObj);
    case 'ne':
      return !todayObj.isSame(newTimeRefObj);
    case 'gt': // todayObj > newTimeRefObj
      return todayObj.isAfter(newTimeRefObj);
    case 'lt': // todayObj < newTimeRefObj
      return todayObj.isBefore(newTimeRefObj);
  }
};

export const injectMarkerStyles = (
  timedStyles: MapMarkerTimedInterface[],
  timeReference?: MapMarkerTimeReferenceInterface,
) => {
  // Terminate early if timedStyles is empty
  if (timedStyles.length === 0 || !timeReference) {
    return;
  }

  // Create <style></style>
  const styleElement = document.createElement('style');
  let cssRule = '';
  const classNames: string[] = [];

  timedStyles.forEach((timedStyle) => {
    if (!isTriggered(timeReference, timedStyle.condition) || !timedStyle.style) {
      return;
    }

    const className = 'marker-style-' + Math.random().toString(36).slice(2, -1);
    classNames.push(className);
    cssRule += `.${className} {
${timedStyle.style}
}
`;
  });

  if (cssRule.length === 0) {
    return;
  }

  styleElement.innerHTML = cssRule;
  document.head.appendChild(styleElement);

  return classNames.join(' ');
};

export const getTimedOpacity = (
  timedStyles: MapMarkerTimedInterface[],
  timeReference?: MapMarkerTimeReferenceInterface,
): number => {
  // Terminate early if timedStyles is empty
  if (timedStyles.length === 0 || !timeReference) {
    return 1;
  }

  const activated = timedStyles.filter(
    (timedStyle) => isTriggered(timeReference, timedStyle.condition) && !!timedStyle.opacity,
  );

  if (activated.length == 0) {
    return 1;
  }

  return activated[0].opacity || 1;
};

export const pointToLayer = (feature: any, latlng: any) => {
  return marker(latlng, {
    // icon: pointIcon(
    //   feature.properties['marker-color'],
    // ),
    opacity: feature.properties.map_marker_config?.timed
      ? getTimedOpacity(
          feature.properties.map_marker_config.timed,
          feature.properties.time_reference,
        )
      : 1,
    icon: feature.properties.map_marker_config
      ? L.ExtraMarkers.icon({
          prefix: feature.properties.map_marker_config.default.prefix,
          icon: feature.properties.map_marker_config.default.icon,
          svg: true,
          shape: feature.properties.map_marker_config.default.shape,
          markerColor: `#${feature.properties['marker-color']}`,
          iconColor: feature.properties.map_marker_config.default.icon_color,
          className: feature.properties.map_marker_config.timed
            ? injectMarkerStyles(
                feature.properties.map_marker_config.timed,
                feature.properties.time_reference,
              )
            : '',
        })
      : L.ExtraMarkers.icon({
          prefix: 'fa',
          icon: 'fa-circle',
          svg: true,
          shape: 'circle',
          markerColor: `#${feature.properties['marker-color']}`,
          iconColor: 'white',
        }),
  });
};

export const mapboxStaticTiles = (name: string, mapid: string, code: string, visible = true) => {
  return {
    name,
    url: 'https://api.mapbox.com/styles/v1/{mapid}/tiles/{z}/{x}/{y}?access_token={apikey}',
    layerOptions: {
      apikey: EnvService.ENV.mapbox_api_key,
      tileSize: 512,
      maxZoom: 22, // "Default zoom level" input field in general settings
      zoomOffset: -1,
      mapid: mapid,
      attribution:
        '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
    },
    visible,
    code,
  };
};

export const getMapLayers = () => {
  return {
    baselayers: {
      satellite: mapboxStaticTiles('Satellite', 'mapbox/satellite-v9', 'satellite'),
      MapQuestAerial: mapboxStaticTiles(
        'Satellite',
        'mapbox/satellite-v9',
        'MapQuestAerial',
        false,
      ),
      streets: mapboxStaticTiles('Streets', 'mapbox/streets-v11', 'streets'),
      MapQuest: mapboxStaticTiles('Streets', 'mapbox/streets-v11', 'MapQuest', false),
      hOSM: {
        name: 'Humanitarian',
        url: '//{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
        layerOptions: {
          attribution:
            '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>, &copy; <a href="http://hot.openstreetmap.org/">Humanitarian OpenStreetMap</a> | <a href="https://www.mapbox.com/feedback/" target="_blank">Improve the underlying map</a>',
        },
        visible: true,
        code: 'hOSM',
      },
    },
  };
};
